import React, { useMemo, useState } from 'react';
import CustomSelector from '@shared/components/CustomSelector';
import _ from 'lodash';
import { CLASSIFICATION_CALCULATION_TYPE } from '../constants/classificationCalculationType';

/**
 * @typedef {object} DataCorrelationReportFiltersSelectedFilter
 * @property {number} [practiceAreaId]
 * @property {number} [classificationMetricId]
 */

/**
 * @typedef {object} DataCorrelationReportFiltersProps
 * @property {DataCorrelationReportFiltersSelectedFilter} selectedFilter
 * @property {(update: *) => void} setSelectedFilter
 * @property {Array<*>} [practiceAreas]
 * @property {Array<*>} [classificationMetrics]
 * @property {boolean} [isLoading]
 */

/**
 * @param {DataCorrelationReportFiltersProps} props
 */
const DataCorrelationReportFilters = props => {
  const { selectedFilter, setSelectedFilter, practiceAreas, classificationMetrics, isLoading } =
    props;

  const [selectedMetricName, setSelectedMetricName] = useState(null);

  const practiceAreaOptions = useMemo(() => getPracticeAreaOptions(practiceAreas), [practiceAreas]);

  const metricNameOptions = useMemo(
    () => getMetricNameOptions(classificationMetrics),
    [classificationMetrics]
  );

  const isMetricTotal = useMemo(() => {
    return classificationMetrics.some(
      metric =>
        metric.classificationMetricName === selectedMetricName &&
        metric.classificationCalculationType === CLASSIFICATION_CALCULATION_TYPE.TOTAL
    );
  }, [selectedMetricName, classificationMetrics]);

  const numDemOptions = useMemo(() => {
    if (!selectedMetricName || isMetricTotal) {
      return [];
    }

    return getNumeratorDenominatorOptions(classificationMetrics, selectedMetricName);
  }, [selectedMetricName, classificationMetrics, isMetricTotal]);

  const handlePracticeAreaChange = practiceAreaId => {
    setSelectedFilter(prev => ({ ...prev, practiceAreaId }));
  };

  const handleMetricNameChange = name => {
    setSelectedMetricName(name);

    const selected = classificationMetrics.find(m => m.classificationMetricName === name);

    const metricId =
      selected.classificationCalculationType === CLASSIFICATION_CALCULATION_TYPE.TOTAL
        ? selected.id
        : null;

    setSelectedFilter(prev => ({ ...prev, classificationMetricId: metricId }));
  };

  const handleNumDemChange = classificationMetricId => {
    setSelectedFilter(prev => ({ ...prev, classificationMetricId }));
  };

  return (
    <div className='report-filters'>
      <div className='selector-group'>
        <CustomSelector
          testId='practice-area-selector'
          title='Practice Area'
          showAsterisk={false}
          placeholder='Select a Practice Area'
          loading={isLoading}
          options={practiceAreaOptions}
          value={selectedFilter.practiceAreaId}
          setValue={handlePracticeAreaChange}
        />
      </div>

      <div className='selector-group'>
        <CustomSelector
          testId='classification-metric-name-selector'
          title='Metric'
          showAsterisk={false}
          placeholder='Select a Metric to Compare'
          loading={isLoading}
          options={metricNameOptions}
          value={selectedMetricName}
          setValue={handleMetricNameChange}
        />
      </div>

      <div className='selector-group'>
        <CustomSelector
          testId='classification-num-dem-selector'
          title='Metric Numerator/Denominator'
          showAsterisk={false}
          placeholder='Select a Num / Dem to Compare'
          loading={isLoading}
          options={numDemOptions}
          disabled={!selectedMetricName || isMetricTotal}
          value={selectedFilter.classificationMetricId}
          setValue={handleNumDemChange}
        />
      </div>
    </div>
  );
};

const getPracticeAreaOptions = practiceAreas =>
  _.chain(practiceAreas)
    .map(pa => ({
      title: `${pa.Name} (${pa.Acronym})`,
      value: pa.InternalId
    }))
    .orderBy(opt => opt.title)
    .value();

const getMetricNameOptions = classificationMetrics =>
  _(classificationMetrics)
    .map(metric => metric.classificationMetricName)
    .uniq()
    .sort()
    .map(name => ({
      title: name,
      value: name
    }))
    .value();

const getNumeratorDenominatorOptions = (classificationMetrics, metricName) => {
  return _.chain(classificationMetrics)
    .filter(metric => metric.classificationMetricName === metricName)
    .map(metric => ({
      value: metric.id,
      title: `${metric.classificationNumerator} / ${metric.classificationDenominator}`
    }))
    .sortBy(x => x.title)
    .value();
};

export default DataCorrelationReportFilters;
